/**
 * Admin -> Settings -> Integrations: Google akkaunt (OAuth). Import Google Sheets'ni shu akkaunt nomidan o'qiydi.
 * "Connect" Google rozilik sahifasiga olib boradi; qaytgach ?google=... natijasi toast bilan ko'rsatiladi.
 * Faqat o'qish ruxsati so'raladi; token brauzerga hech qachon kelmaydi.
 */
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Check, Link2, RefreshCw, Unlink } from "lucide-react";
import { ApiError } from "@/lib/api";
import { cn, formatDateTime, formatWhen } from "@/lib/utils";
import { useGoogleConnect, useGoogleDisconnect, useGoogleStatus, useSheetsStatus, type GoogleStatus } from "@/features/integrations/api";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { Avatar, ErrorState, Spinner } from "@/components/ui/misc";
import { Modal } from "@/components/ui/modal";
import { toast } from "@/components/ui/toast";
import { CopyField } from "./IntegrationControls";

type PillKey = "connected" | "reauth" | "notConnected" | "notConfigured";

function pillFor(s: GoogleStatus): { key: PillKey; cls: string } {
  if (s.status === "connected") return { key: "connected", cls: "bg-status-hired text-ink" };
  if (s.status === "needs_reauth") return { key: "reauth", cls: "bg-status-rejected text-ink" };
  if (s.configured) return { key: "notConnected", cls: "bg-fill text-ink" };
  return { key: "notConfigured", cls: "bg-fill text-ink" };
}

export function GoogleCard() {
  const { t } = useTranslation("settings");
  const status = useGoogleStatus();
  const sheets = useSheetsStatus();
  const connect = useGoogleConnect();
  const disconnect = useGoogleDisconnect();
  const [redirecting, setRedirecting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  useOAuthResult();

  if (status.isPending || sheets.isPending) {
    return (
      <Card>
        <CardBody className="flex justify-center py-6">
          <Spinner />
        </CardBody>
      </Card>
    );
  }
  if (status.isError) return <ErrorState message={status.error.message} onRetry={() => status.refetch()} />;
  const s = status.data;
  // Import ham, nusxa ham service account orqali ishlaydi: OAuth sozlanmagan va ulanmagan bo'lsa karta faqat chalg'itadi (2026-10-25)
  if (!s.configured && s.status === "disconnected" && sheets.data?.configured) return null;
  const pill = pillFor(s);

  async function startConnect() {
    try {
      const { auth_url } = await connect.mutateAsync();
      setRedirecting(true);
      window.location.assign(auth_url);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t("google.connectFailed"));
    }
  }

  async function doDisconnect() {
    try {
      await disconnect.mutateAsync();
      setConfirmOpen(false);
      toast.success(t("google.disconnected"));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t("google.disconnectFailed"));
    }
  }

  const busy = connect.isPending || redirecting;

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            {t("google.title")} <span className={cn("rounded-full px-2.5 py-0.5 text-[11px] font-semibold", pill.cls)}>{t(`google.pill.${pill.key}`)}</span>
          </span>
        }
        subtitle={t("google.subtitle")}
      />
      <CardBody className="space-y-4">
        {!s.configured && (
          <>
            <p className="text-[13px] text-ink-soft">{t("google.notConfiguredDesc")}</p>
            <CopyField label={t("google.redirectUri")} value={s.redirect_uri} />
            <p className="text-[12px] text-muted">{t("google.setupDoc")}</p>
          </>
        )}

        {s.configured && s.status === "disconnected" && (
          <>
            <p className="text-[13px] text-ink-soft">{t("google.whatFor")}</p>
            <Permissions />
            <div>
              <Button variant="primary" loading={busy} onClick={startConnect}>
                <Link2 size={14} /> {t("google.connect")}
              </Button>
            </div>
          </>
        )}

        {s.configured && s.status !== "disconnected" && (
          <>
            {s.status === "needs_reauth" && (
              <div className="flex gap-2 rounded-[10px] border border-danger bg-danger-soft px-3 py-2.5 text-[13px]">
                <AlertTriangle size={16} className="mt-0.5 shrink-0 text-danger" />
                <div>
                  <div className="font-semibold text-danger">{t("google.reauthTitle")}</div>
                  <div className="text-ink-soft">{t("google.reauthDesc")}</div>
                  {s.last_error && (
                    <div className="mt-1 text-[11px] text-muted">
                      {formatDateTime(s.last_error_at)} · {s.last_error}
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="flex items-center gap-3 rounded-[10px] border border-line px-3 py-2.5">
              <Avatar initials={(s.name || s.email).slice(0, 1).toUpperCase()} name={s.name || s.email} />
              <div className="min-w-0">
                <div className="truncate text-[13px] font-semibold">{s.name || s.email}</div>
                <div className="truncate text-[12px] text-muted">{s.email}</div>
              </div>
              <div className="ml-auto text-right text-[12px] text-muted">
                {t("google.connectedBy", { who: s.connected_by ?? "—" })}
                <div>{formatDateTime(s.connected_at)}</div>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 text-[12px] sm:grid-cols-2">
              <div>
                <div className="text-muted">{t("google.access")}</div>
                <div className="mt-0.5 flex items-center gap-1">
                  {s.can_read_sheets ? <Check size={14} className="text-success" /> : <AlertTriangle size={14} className="text-warning" />}
                  {s.can_read_sheets ? t("google.sheetsReadOnly") : t("google.noSheetsAccess")}
                </div>
              </div>
              <div>
                <div className="text-muted">{t("google.lastUsed")}</div>
                <div className="mt-0.5">{s.last_used_at ? formatWhen(s.last_used_at) : t("google.neverUsed")}</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant={s.status === "needs_reauth" ? "primary" : "secondary"} loading={busy} onClick={startConnect}>
                <RefreshCw size={14} /> {s.status === "needs_reauth" ? t("google.reconnect") : t("google.switchAccount")}
              </Button>
              <Button variant="danger" onClick={() => setConfirmOpen(true)}>
                <Unlink size={14} /> {t("google.disconnect")}
              </Button>
            </div>
          </>
        )}

        {s.configured && (
          <details className="text-[12px]">
            <summary className="cursor-pointer text-muted hover:text-ink">{t("google.technical")}</summary>
            <div className="mt-2 space-y-2">
              <CopyField label={t("google.redirectUri")} value={s.redirect_uri} />
              <div className="text-muted">
                {t("google.clientId")}: <span className="font-mono">{s.client_id_masked}</span>
              </div>
            </div>
          </details>
        )}
      </CardBody>

      <Modal
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        size="sm"
        title={t("google.disconnectTitle")}
        footer={
          <>
            <Button onClick={() => setConfirmOpen(false)}>{t("common:actions.cancel")}</Button>
            <Button variant="danger" loading={disconnect.isPending} onClick={doDisconnect}>
              {t("google.disconnect")}
            </Button>
          </>
        }
      >
        <p className="text-[13px] text-ink-soft">{t("google.disconnectDesc", { email: s.email })}</p>
      </Modal>
    </Card>
  );
}

function Permissions() {
  const { t } = useTranslation("settings");
  return (
    <ul className="space-y-1 text-[12px] text-ink-soft">
      {(["profile", "sheets"] as const).map((k) => (
        <li key={k} className="flex items-center gap-1.5">
          <Check size={14} className="text-success" /> {t(`google.permission.${k}`)}
        </li>
      ))}
    </ul>
  );
}

/** Google'dan qaytish natijasi (?google=connected|cancelled|error&reason=...): bir marta toast, keyin URL tozalanadi */
function useOAuthResult() {
  const { t } = useTranslation("settings");
  const [params, setParams] = useSearchParams();
  const handled = useRef<string | null>(null);
  const result = params.get("google");
  const reason = params.get("reason") ?? "";
  useEffect(() => {
    if (!result) return;
    const key = `${result}:${reason}`;
    if (handled.current === key) return; // StrictMode'da effekt ikki marta ishlaydi
    handled.current = key;
    if (result === "connected") toast.success(t("google.result.connected"), t("google.result.connectedDesc"));
    else if (result === "cancelled") toast.info(t("google.result.cancelled"));
    else toast.error(t("google.result.failed"), t(`google.reason.${reason}`, { defaultValue: t("google.reason.unknown", { code: reason || "?" }) }));
    const next = new URLSearchParams(params);
    next.delete("google");
    next.delete("reason");
    setParams(next, { replace: true });
  }, [result, reason, params, setParams, t]);
}
